// src/pages/student-portal/enrollments/EnrollmentsPage.js
// Student Portal version (purple theme)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Tooltip,
    Skeleton,
    useMediaQuery,
    Card,
    CardContent,
    Grid,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

const STATUS_CONFIG = {
    active: { label: 'Active', color: '#10b981', bg: '#dcfce7', icon: PlayArrowIcon },
    in_progress: { label: 'In Progress', color: '#10b981', bg: '#dcfce7', icon: PlayArrowIcon },
    completed: { label: 'Completed', color: '#3b82f6', bg: '#dbeafe', icon: CheckCircleIcon },
    dropped: { label: 'Dropped', color: '#ef4444', bg: '#fee2e2', icon: CancelIcon },
    expired: { label: 'Expired', color: '#f59e0b', bg: '#fef3c7', icon: AccessTimeIcon },
};

function getStatusConfig(status) {
    return STATUS_CONFIG[(status || 'active').toLowerCase()] || STATUS_CONFIG.active;
}

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

function formatRelativeDate(dateStr) {
    if (!dateStr) return 'Never';
    try {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(dateStr);
    } catch {
        return dateStr;
    }
}

// Mobile card view for smaller screens
const EnrollmentCard = ({ enrollment, onClick }) => {
    const statusCfg = getStatusConfig(enrollment.status);
    const progress = enrollment.progress || enrollment.completion_percentage || 0;
    const courseName = enrollment.course_name || enrollment.course_title || enrollment.title || 'Untitled Course';

    return (
        <Card
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: ACCENT,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                },
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            color: '#1e293b',
                            fontSize: '0.95rem',
                            flex: 1,
                            mr: 1,
                        }}
                    >
                        {courseName}
                    </Typography>
                    <Chip
                        label={statusCfg.label}
                        size="small"
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            bgcolor: statusCfg.bg,
                            color: statusCfg.color,
                            height: 22,
                        }}
                    />
                </Box>

                {/* Progress */}
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>Progress</Typography>
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 700, color: '#475569' }}>
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: progress >= 100 ? ACCENT : '#3b82f6',
                                borderRadius: 3,
                            },
                        }}
                    />
                </Box>

                {/* Dates */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                            Enrolled: {formatDate(enrollment.enrolled_date || enrollment.enrolled_at || enrollment.created_at)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                            Last: {formatRelativeDate(enrollment.last_accessed || enrollment.last_activity)}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default function EnrollmentsPage() {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');

    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchEnrollments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/lms/portal/enrollments');
            const data = response.data;
            setEnrollments(data.enrollments || data.data || data.results || []);
        } catch (err) {
            console.error('Failed to fetch enrollments:', err);
            setError(err.response?.data?.message || 'Failed to load enrollments.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);

    const handleRowClick = useCallback((enrollment) => {
        const courseId = enrollment.course_slug || enrollment.course_id || enrollment.id || enrollment._id;
        navigate(`/student-portal/courses/${courseId}`);
    }, [navigate]);

    // Stats
    const totalEnrollments = enrollments.length;
    const activeCount = enrollments.filter(
        (e) => e.status === 'active' || e.status === 'in_progress'
    ).length;
    const completedCount = enrollments.filter((e) => e.status === 'completed').length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}
                        >
                            My Enrollments
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                            Track all your course enrollments
                        </Typography>
                    </Box>
                </Box>

                {/* Quick stats in header */}
                <Box sx={{ display: 'flex', gap: 3, mt: 2.5, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Total', value: totalEnrollments },
                        { label: 'Active', value: activeCount },
                        { label: 'Completed', value: completedCount },
                    ].map((stat) => (
                        <Box
                            key={stat.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: '12px',
                                px: 2,
                                py: 1,
                            }}
                        >
                            <Typography sx={{ fontFamily, color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                                {stat.value}
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                                {stat.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchEnrollments} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading ? (
                    isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Card key={i} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 1 }} />
                                        <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3, mb: 1.5 }} />
                                        <Skeleton variant="text" width="60%" sx={{ fontSize: '0.75rem' }} />
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {['Course', 'Enrolled', 'Progress', 'Status', 'Last Accessed'].map((h) => (
                                            <TableCell key={h}>
                                                <Skeleton variant="text" width={80} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {Array.from({ length: 5 }).map((_, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton variant="text" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )
                ) : enrollments.length === 0 ? (
                    /* Empty State */
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No enrollments yet
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                            Browse courses and enroll to start your learning journey!
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/student-portal/courses')}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                borderRadius: '12px',
                                px: 4,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#7c3aed' },
                            }}
                        >
                            Browse Courses
                        </Button>
                    </Paper>
                ) : isMobile ? (
                    /* Mobile: Card list */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {enrollments.map((enrollment, index) => (
                            <EnrollmentCard
                                key={enrollment.id || enrollment._id || index}
                                enrollment={enrollment}
                                onClick={() => handleRowClick(enrollment)}
                            />
                        ))}
                    </Box>
                ) : (
                    /* Desktop: Table */
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        '& th': {
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#64748b',
                                            fontSize: '0.8rem',
                                            bgcolor: '#f8fafc',
                                            borderBottom: '2px solid #e2e8f0',
                                            py: 1.5,
                                        },
                                    }}
                                >
                                    <TableCell>Course Name</TableCell>
                                    <TableCell align="center">Enrolled Date</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 180 }}>Progress</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Last Accessed</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {enrollments.map((enrollment, index) => {
                                    const statusCfg = getStatusConfig(enrollment.status);
                                    const progress = enrollment.progress || enrollment.completion_percentage || 0;
                                    const courseName = enrollment.course_name || enrollment.course_title || enrollment.title || 'Untitled Course';
                                    const StatusIcon = statusCfg.icon;

                                    return (
                                        <TableRow
                                            key={enrollment.id || enrollment._id || index}
                                            sx={{
                                                cursor: 'pointer',
                                                '& td': {
                                                    fontFamily,
                                                    py: 2,
                                                    borderBottom: '1px solid #f1f5f9',
                                                },
                                                '&:hover': {
                                                    bgcolor: '#f8fafc',
                                                },
                                                transition: 'background-color 0.2s',
                                            }}
                                            onClick={() => handleRowClick(enrollment)}
                                        >
                                            {/* Course Name */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '10px',
                                                            bgcolor: `${ACCENT}12`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <SchoolIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 600,
                                                            color: '#1e293b',
                                                            fontSize: '0.88rem',
                                                        }}
                                                    >
                                                        {courseName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Enrolled Date */}
                                            <TableCell align="center">
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                    {formatDate(enrollment.enrolled_date || enrollment.enrolled_at || enrollment.created_at)}
                                                </Typography>
                                            </TableCell>

                                            {/* Progress */}
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(progress, 100)}
                                                        sx={{
                                                            flexGrow: 1,
                                                            height: 8,
                                                            borderRadius: 4,
                                                            bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: progress >= 100 ? ACCENT : '#3b82f6',
                                                                borderRadius: 4,
                                                            },
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 700,
                                                            color: progress >= 100 ? ACCENT : '#475569',
                                                            fontSize: '0.82rem',
                                                            minWidth: 40,
                                                        }}
                                                    >
                                                        {Math.round(progress)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell align="center">
                                                <Chip
                                                    icon={<StatusIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={statusCfg.label}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.72rem',
                                                        bgcolor: statusCfg.bg,
                                                        color: statusCfg.color,
                                                        height: 26,
                                                        '& .MuiChip-icon': { color: statusCfg.color },
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Last Accessed */}
                                            <TableCell align="center">
                                                <Tooltip
                                                    title={formatDate(enrollment.last_accessed || enrollment.last_activity)}
                                                    placement="top"
                                                >
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.82rem' }}>
                                                        {formatRelativeDate(enrollment.last_accessed || enrollment.last_activity)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
}
